
<template>
  <BaseModal
    card-width="600px"
    no-padding
    no-toolbar-padding
    no-exit
    @close="$emit('close')"
  >
    <template #toolbar>
      <div class="w-full pb-5 relative">
        <div class="space-y-1">
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Start Spying <span v-if="!loading && !isNewSpyder">on {{ brand?.name }}</span>
          </BaseText>
          <BaseText
            size="sm"
            class="text-text-normal"
          >
            Track & save every ad <span v-if="!loading && !isNewSpyder">{{ brand?.name }}</span> launches, and more...
          </BaseText>
        </div>

        <button
          class="absolute top-0 right-0 p-1.5"
          @click="$emit('close')"
        >
          <TagRemoveIcon stroke="#5E6678" />
        </button>
      </div>
    </template>
    <template #default>
      <div
        class="p-5 space-y-5 border-t border-border-normal"
      >
        <!-- Loading skeleton -->
        <div
          v-if="loading"
          class="flex items-start gap-3"
        >
          <div class="skeleton w-10 h-10 rounded-lg" />
          <div class="space-y-2 flex flex-col">
            <div class="skeleton w-10 h-3 rounded-md" />
            <div class="skeleton w-12 h-3 rounded-md" />
          </div>
        </div>
        <!-- Known brand (not new spyder) -->
        <div
          v-else-if="!isNewSpyder"
          class="flex items-start gap-3"
        >
          <BrandAvatar
            :avatar-src="brand?.avatar"
            :brand-name="brandName"
            :width="40"
            :height="40"
            class="rounded-lg"
          />
          <div>
            <BaseText
              type="label"
              class="text-text-muted"
            >
              {{ brandName }}
            </BaseText>
            <BaseText
              v-if="brand?.domain"
              size="sm"
              class="text-text-normal"
            >
              {{ brand.domain }}
            </BaseText>
          </div>
        </div>
        <!-- New spyder (404 case) -->
        <div
          v-else
          class="flex items-start gap-2"
        >
          <MetaIcon class="w-10 h-10 text-primary-blue-100 -mt-1.5" />
          <div class="w-full space-y-1">
            <input
              :value="adLibraryUrl"
              class="max-w-none w-full focus:outline-none form-input p-1 cursor-pointer text-text-subdued"
              placeholder="facebook.com/ads/library..."
              readonly
              @click="openAdLibraryUrl"
            >
            <BaseText
              size="xs"
              class="text-text-subdued"
            >
              Brand not yet tracked. Add it to your spyder to start tracking ads.
            </BaseText>
          </div>
        </div>
        <!-- folder -->
        <div class="w-full">
          <BaseText
            type="label"
            size="sm"
            class="w-full text-text-muted relative left-0.5"
          >
            Location
          </BaseText>
          <div class="relative w-full h-8">
            <div class="absolute top-0 left-0 right-0 flex flex-col flex-grow mt-0.5 custom-shadow rounded-lg overflow-hidden">
              <button
                class="flex items-center w-full px-2 py-1.5 flex-nowrap"
                @click="foldersExpanded = !foldersExpanded"
              >
                <FolderIcon class="text-icon-normal mr-2 flex-shrink-0" />
                <BaseText
                  type="body"
                  size="sm"
                  class="flex-grow text-text-muted truncate text-left mr-2"
                >
                  {{ selectedFolder.name || '' }}
                </BaseText>
                <div
                  class="transform transition-transform flex-shrink-0"
                  :style="{transform: foldersExpanded ? 'scaleY(-1)' : ''}"
                >
                  <ChevronIcon class="text-icon-normal" />
                </div>
              </button>
              <div
                v-on-clickaway="() => { foldersExpanded = false }"
                class="overflow-hidden rounded-b-lg border-t"
                :class="foldersExpanded ? 'border-border-normal' : 'border-transparent'"
              >
                <div
                  class="folder-dropdown flex flex-col gap-0.5 px-0.5 rounded-b-lg bg-white cursor-pointer overflow-y-scroll scrollbar-hide"
                  :class="{'expanded': foldersExpanded}"
                  :style="{height: foldersExpanded ? `${Math.min(folderOptions.length * 34 + 6, 158)}px` : '0px'}"
                >
                  <button
                    v-for="(folder, index) in folderOptions"
                    :key="`change-folder-${index}`"
                    class="group flex items-center rounded-lg px-1.5 py-1.5 transition duration-100 hover:bg-neutral-10 whitespace-nowrap"
                    :class="`${index === 0 ? 'mt-1' : index === folderOptions.length - 1 ? 'mb-1' : ''} ${foldersExpanded ? 'opacity-100' : 'opacity-0'}`"
                    @click="selectedFolder = folder, foldersExpanded = false"
                  >
                    <FolderIcon class="transition-colors text-neutral-400 group-hover:text-icon-normal mr-2 flex-shrink-0" />
                    <BaseText
                      type="body"
                      size="sm"
                      class="text-text-muted flex-grow truncate text-left"
                    >
                      {{ folder.name }}
                    </BaseText>
                  </button>
                </div>
                <div
                  v-if="foldersExpanded && folderOptions.length > 4"
                  style="z-index: 100"
                  class="absolute left-0 bottom-0 right-0 h-4 bg-gradient-to-t from-white to-transparent opacity-70 pointer-events-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- footer -->
      <div class="p-5 border-t border-border-normal flex items-center justify-between">
        <div class="flex items-center gap-1.5 rounded-full border border-border-normal pl-0.5 py-0.5 pr-3">
          <img
            src="../../assets/icons/spyder-token.png"
            class="w-7 h-7"
          >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            {{ getSpyderCreditsUsed }}/{{ getSpyderLimit }} credits
          </BaseText>
        </div>
        <BaseButton
          primary
          :disabled="loadingAddBrand"
          :loading="loadingAddBrand"
          @click="handleAddBrand"
        >
          Start Tracking
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import ForeplayAPI from '@/api/foreplayServer'
import { normalizeBrandsForSubscriptionsList } from '@/utils/spyder/brandUtils'

import BrandAvatar from '../brands/BrandAvatar.vue'

// Icons
import FolderIcon from '../globals/Icons/SpyderV2Icons/FolderIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import MetaIcon from '../globals/Icons/OnboardIcons/MetaIcon.vue'

export default {
  name: 'SpyderBrandModal',
  components: {
    BrandAvatar,
    FolderIcon,
    ChevronIcon,
    TagRemoveIcon,
    MetaIcon
  },
  mixins: [clickaway],
  props: {
    pageId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      brand: null,
      loading: true,
      isNewSpyder: false,
      loadingAddBrand: false,
      // location
      selectedFolder: { id: null, name: 'Default Folder' },
      foldersExpanded: false

    }
  },
  computed: {
    ...mapGetters('BoardsModule', ['getFolders']),
    ...mapGetters('AuthModule', ['getSpyderLimit']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getPendingSpyderBrands', 'getSpyderCreditsUsed']),
    // The brand this page id belongs to, if it's already tracked (or still being added)
    trackedBrand () {
      return normalizeBrandsForSubscriptionsList(this.getSpyderSubscriptions, this.getPendingSpyderBrands)
        .find(brand => brand.advertiserIds.includes(this.pageId)) || null
    },
    brandName () {
      return this.brand?.name || 'New Brand'
    },
    adLibraryUrl () {
      return `https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=ALL&view_all_page_id=${this.pageId}&search_type=page&media_type=all`
    },
    folderOptions () {
      const folders = this.getFolders.filter(folder => folder.id !== this.selectedFolder.id)
      if (this.selectedFolder.id) {
        folders.push({ id: null, name: 'Default Folder' })
      }
      return folders.sort((a, b) => a.name.localeCompare(b.name)) // sort alphabetically
    }
  },
  async mounted () {
    if (!this.pageId) {
      this.$showAlert({ type: 'error', message: 'Error preloading brand.' })
      this.$emit('close')
      return
    }
    try {
      if (this.trackedBrand?.pending) {
        this.$showAlert({ type: 'info', message: `${this.trackedBrand.name || 'This brand'} is already being added to Spyder.` })
        this.$emit('close')
        return
      }
      if (this.trackedBrand) {
        this.$showAlert({ type: 'info', message: 'Redirecting to spyder...' })
        this.$router.push({ name: 'SpyderView', params: { id: this.trackedBrand.id } })
        this.$emit('close')
        return
      }

      const brand = await ForeplayAPI.Brands.getByAdvertiserId(this.pageId)
      if (!brand) {
        // We don't have this page yet: subscribing builds the brand
        this.isNewSpyder = true
        return
      }
      this.brand = {
        name: brand.name,
        avatar: brand.avatar_url,
        // Primary domain first
        domain: brand.domains?.[0]?.domain || null
      }
    } catch (error) {
      console.error(error)
      const errMessage = error.response?.data?.message || 'Error loading brand.'
      this.$showAlert({ type: 'error', message: errMessage })
      this.$emit('close')
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    ...mapActions('BoardsModule', ['fetchFolders']),
    async handleAddBrand () {
      if (this.loadingAddBrand) return
      // The Spyder API doesn't enforce credit limits, so check here like the add modal does
      if (this.getSpyderCreditsUsed >= this.getSpyderLimit) {
        this.$showAlert({ type: 'error', message: 'You have no Spyder credits left.' })
        return
      }
      this.loadingAddBrand = true

      try {
        const { data: result } = await ForeplayAPI.Spyder.subscribeToAdvertisers([this.pageId], this.selectedFolder?.id || null)
        await this.fetchSpyderSubscriptions()
        await this.fetchFolders()
        // A page we don't have yet comes back pending while the brand is built
        const pending = result?.pending?.[0]
        if (pending) {
          this.$showAlert({
            type: 'info',
            message: `We're adding ${pending.name || 'this brand'} to Spyder. This can take a few minutes.`
          })
        }
        this.$emit('spyderAdded')
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error.response?.data?.message || 'Unable to track brand. If you think this is a mistake, contact support.'
        })
      } finally {
        this.loadingAddBrand = false
        this.$emit('close')
      }
    },
    openAdLibraryUrl () {
      window.open(this.adLibraryUrl, '_blank')
    }
  }
}
</script>

<style scoped>
.folder-dropdown {
  position: relative;
  z-index: 100;
  transition: height 100ms ease-in-out;
}

.custom-shadow {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.form-input {
  color: #5E6678;
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #B2B4C7;
  transition: color 150ms ease-in-out, opacity 150ms ease-in-out;
  opacity: 0.8;
}
.form-input:hover::placeholder {
  opacity: 1;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}
</style>
